import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { LocalizedMessage } from 'intl/LocalizedComponents';
// Lodash
import _get from 'lodash/get';
// Stripe
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements, PaymentElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import { withTheme } from '@material-ui/core/styles';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import { ContainedButton } from 'components/core/Button';
import TextField from 'components/core/TextField';
import Checkbox from 'components/core/Checkbox';
import RadioGroup from 'components/core/RadioGroup';
// Models
import mFinance from 'models/cpm/finance';
import mOnDemand from 'models/cpm/onDemand';
// Custom
import {
  cpmInitiatePayment, cpmUpdateOnDemandAccessCustomer,
} from 'actions';
import { cpmPurchaseShape, meDataShape } from 'constants/PropTypesShapes';
import Callout from 'components/misc/Callout';
import { buildFormErrors, cpmCheckoutOnDemandAccessConstraints } from 'utils/Forms';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';

const STRIPE_ERRORS = {
  CARD_ERROR: 'card_error',
};

const StripeInternalForm = ({
  location: { query },
  fields: { name, cardToUse, email },

  me,
  cpmPayment,
  cpmPaymentError,
  slugId,

  purchase,
  confirmButtonLabel,

  needsOnDemandCustomerInformation,
  withPaymentMethods,

  onDemandCustomerUpdated,
  onDemandCustomerUpdatedError,

  dispatchInitiatePayment,
  dispatchUpdateOnDemandAccessCustomer,

  onSuccess,
  onFutureUsage,

  errors,
  onFieldsChange,
  onErrorsChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const intl = useIntl();

  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentIsProcessing, setPaymentIsProcessing] = useState(false);
  const [stripeFormLoaded, stripeFormIsLoaded] = useState(false);
  const [futureUsage, setFutureUsage] = useState(false);

  const withNewMethodPayment = cardToUse === 'new_payment_method';

  /* Error Message */
  /* ------------ */
  const ErrorMessage = errorMessage ? (
    <div className="mb-3">
      <Callout severity="error" withInfoIcon>
        {errorMessage}
      </Callout>
    </div>
  ) : null;

  /* Additional information for On-Demand */
  /* ------------------------------------ */

  /* Name */
  const Name = (
    <TextField
      label={<LocalizedMessage id="field.purchase.name" />}
      value={name}
      onChange={(newName) => onFieldsChange({ name: newName })}
      errorText={_get(errors, 'name')}
      required
    />
  );

  /* Email */
  const Email = (
    <TextField
      label={<LocalizedMessage id="field.purchase.email" />}
      value={email}
      onChange={(newEmail) => onFieldsChange({ email: newEmail })}
      errorText={_get(errors, 'email')}
      required
    />
  );

  const CustomerInformation = stripeFormLoaded && needsOnDemandCustomerInformation ? (
    <div className="px-2" style={{ marginBottom: 30 }}>
      {Name}
      {Email}
    </div>
  ) : null;

  /* Registered cards */
  /* ---------------- */
  const alreadyRegisteredPaymentMethods = me
    ? me.paymentMethods.map(({
      brand, expMonth, expYear, last4, stripeDetails: { id },
    }) => ({
      key: id,
      value: id,
      label: (
        <LocalizedMessage
          id="info.registeredCard"
          values={{
            brand: brand.charAt(0).toUpperCase() + brand.slice(1),
            expMonth: String(expMonth).padStart(2, '0'),
            expYear,
            last4,
          }}
        />
      ),
    }))
    : [];
  const newPaymentMethod = {
    key: 'new_payment_method',
    value: 'new_payment_method',
    label: (
      <div>
        <LocalizedMessage id="info.newPaymentMethod" />
      </div>
    ),
  };
  const RegisteredCards = withPaymentMethods ? (
    <div className="mb-3">
      <RadioGroup
        value={cardToUse}
        items={[...alreadyRegisteredPaymentMethods, newPaymentMethod]}
        onChange={(value) => onFieldsChange({ cardToUse: value })}
      />
    </div>
  ) : null;

  /* Checkbox to save payment method */
  /* ------------------------------- */
  const shouldSaveCurrentPaymentMethod = withPaymentMethods && withNewMethodPayment && stripeFormLoaded;
  const CheckboxSavePaymentMethod = shouldSaveCurrentPaymentMethod ? (
    <div className="mt-3">
      <Checkbox
        label={<LocalizedMessage id="info.savePaymentInfo" />}
        checked={futureUsage}
        onChange={(value) => {
          setFutureUsage(value);
          onFutureUsage(value);
        }}
      />
    </div>
  ) : null;

  /* Payment button */
  /* -------------- */
  const stripeIsLoading = !stripe || !elements;
  const PaymentButton = stripeFormLoaded ? (
    <div className="text-center" style={{ marginTop: 30 }}>
      <ContainedButton loading={paymentIsProcessing || stripeIsLoading} type="submit" label={confirmButtonLabel} />
    </div>
  ) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (withNewMethodPayment) {
      const { error } = await elements.submit();
      if (error) return;
    }

    if (purchase) {
      // On-demand payment
      if (needsOnDemandCustomerInformation) {
        const clientErrors = buildFormErrors({ name, email }, cpmCheckoutOnDemandAccessConstraints);
        onErrorsChange(clientErrors);

        if (!clientErrors) {
          dispatchUpdateOnDemandAccessCustomer({
            email,
            lastname: name,
            reference: query.onDemandReference,
            slugId,
          });
          setPaymentIsProcessing(true);
        }
      } else {
        // Booking payment
        dispatchInitiatePayment({
          purchaseId: purchase.id,
          savePaymentMethodForFutureUsage: futureUsage,
          paymentMethodId: withNewMethodPayment ? null : cardToUse,
        });
        setPaymentIsProcessing(true);
      }
      setErrorMessage(null);
    }
  };
  /* ⬇️ ⬇️ */
  useEffect(() => {
    if (cpmPayment) {
      const { clientSecret } = cpmPayment.payments[0].stripeDetails;

      const confirmParams = {
        ...(withNewMethodPayment ? { elements } : {}),
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      };

      let ignore = false;
      stripe.confirmPayment(confirmParams).then((result) => {
        const { error } = result;
        if (!ignore) {
          setPaymentIsProcessing(false);
          if (error) {
            switch (error.type) {
              case STRIPE_ERRORS.CARD_ERROR:
                setErrorMessage(error.message);
                break;
              default:
                setErrorMessage(intl.formatMessage({ id: 'error.payment.invalidRequest' }));
                break;
            }
          } else {
            onSuccess(result);
          }
        }
      });

      // eslint-disable-next-line no-return-assign
      return () => (ignore = true);
    }
  }, [cpmPayment, onDemandCustomerUpdated]);
  /* ❌ ❌ */
  useEffect(() => {
    if (onDemandCustomerUpdatedError || cpmPaymentError) setPaymentIsProcessing(false);
    if (cpmPaymentError) setErrorMessage(intl.formatMessage({ id: 'error.payment.cantInitiate' }));
  }, [onDemandCustomerUpdatedError, cpmPaymentError]);

  // If on-demand, update customer information and ONLY THEN initiate payment
  const hasRunOnDemandUpdate = useRef(false);
  React.useEffect(
    function initiatePaymentAfterOnDemandUpdate() {
      if (onDemandCustomerUpdated && !hasRunOnDemandUpdate.current) {
        dispatchInitiatePayment({ purchaseId: purchase.id, savePaymentMethodForFutureUsage: futureUsage });
        hasRunOnDemandUpdate.current = true; // Mark the code block as having run
      }
    },
    [onDemandCustomerUpdated],
  );

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */
  return (
    <LoadingDisplayer loading={!stripeFormLoaded || paymentIsProcessing} mountChildrenWhileLoading>
      {ErrorMessage}
      <form onSubmit={handleSubmit} noValidate>
        {RegisteredCards}
        {CustomerInformation}
        {withNewMethodPayment ? (
          <PaymentElement onReady={() => stripeFormIsLoaded(true)} options={{ readOnly: paymentIsProcessing }} />
        ) : null}
        {CheckboxSavePaymentMethod}
        {PaymentButton}
      </form>
    </LoadingDisplayer>
  );
};

StripeInternalForm.propTypes = {
  confirmButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  fields: PropTypes.object.isRequired,
  cpmPayment: cpmPurchaseShape,
  cpmPaymentError: PropTypes.object,
  me: PropTypes.oneOfType([
    meDataShape,
    PropTypes.shape({
      reference: PropTypes.string,
      email: PropTypes.string,
    }),
  ]),
  errors: PropTypes.object,
  purchase: cpmPurchaseShape.isRequired,
  slugId: PropTypes.number.isRequired,
  location: PropTypes.shape({
    query: PropTypes.object,
  }),
  needsOnDemandCustomerInformation: PropTypes.bool,
  withPaymentMethods: PropTypes.bool,
  onDemandCustomerUpdated: PropTypes.bool,
  onDemandCustomerUpdatedError: PropTypes.bool,
  onFieldsChange: PropTypes.func.isRequired,
  onFutureUsage: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  dispatchInitiatePayment: PropTypes.func.isRequired,
  dispatchUpdateOnDemandAccessCustomer: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
};

StripeInternalForm.defaultProps = {
  me: null,
  cpmPayment: null,
  cpmPaymentError: null,
  needsOnDemandCustomerInformation: false,
  withPaymentMethods: false,
  errors: null,
  location: null,
  onDemandCustomerUpdated: false,
  onDemandCustomerUpdatedError: false,
};

/* ------------------- */
/* Stripe Payment Form */
/* ------------------- */

const STRIPE_PK = __STRIPE_PK__; // eslint-disable-line no-undef
loadStripe.setLoadParameters({ advancedFraudSignals: false }); // Disabling advanced fraud detection signals
const stripePromise = loadStripe(STRIPE_PK);
const stripeOptionsReducer = (state, action) => ({ ...state, ...action });

const StripePaymentForm = ({
  purchase, locale, theme, ...otherProps
}) => {
  const stripeDefaultOptions = {
    locale,
    mode: 'payment',
    amount: purchase.totalAmountInclTax,
    currency: purchase.priceRule.currency.toLowerCase(),
    setupFutureUsage: null,
    loader: 'always',
    appearance: {
      theme: 'stripe',
      labels: 'floating',
      variables: {
        fontFamily: 'Roboto, sans-serif',
        fontSizeBase: '1rem',
      },
      rules: {
        '.Error': {
          color: theme.palette.error.main,
        },
      },
    },
  };

  const [stripeOptions, dispatch] = React.useReducer(stripeOptionsReducer, stripeDefaultOptions);

  const handleFutureUsage = (checked) => dispatch({ setupFutureUsage: checked ? 'on_session' : null });

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <StripeInternalForm {...otherProps} purchase={purchase} onFutureUsage={handleFutureUsage} />
    </Elements>
  );
};

StripePaymentForm.propTypes = {
  purchase: cpmPurchaseShape.isRequired,
  locale: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
};

const mapFieldsToProps = {
  name: {
    defaultValue: '',
  },
  cardToUse: {
    defaultValue: 'new_payment_method',
  },
  email: {
    defaultValue: '',
  },
};

const mapStateToProps = (state) => ({
  cpmPayment: _get(state, 'cpmFinance.purchase.result'),
  cpmPaymentError: _get(state, 'cpmFinance.purchase.error'),

  me: _get(state, 'me.data'),
  locale: _get(state, 'intl.locale'),

  slugId: _get(state, 'cpmSlug.current.result.id'),

  onDemandCustomerUpdated: _get(state, 'cpmOnDemand.update.updated'),
  onDemandCustomerUpdatedError: _get(state, 'cpmOnDemand.update.error'),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchInitiatePayment: (data) => dispatch(cpmInitiatePayment(mFinance.mutInitiatePayment, data)),
  dispatchUpdateOnDemandAccessCustomer: (data) => dispatch(cpmUpdateOnDemandAccessCustomer(mOnDemand.mutUpdateOnDemandAccessCustomer, data)),
});

module.exports = compose(
  withRouter,
  withFormManagement(mapFieldsToProps, null, false),
  withTheme,
  connect(mapStateToProps, mapDispatchToProps),
  withStateReinit(['initiatePayment']),
)(StripePaymentForm);



// WEBPACK FOOTER //
// ./components/payment/StripePaymentForm.js